// Audit Log Utility
// This file handles all audit log operations for user management
export interface AuditLogEntry {
  id: string
  timestamp: string
  userId: string
  userName: string
  fieldName: string
  oldValue: string
  newValue: string
  source: 'BO_USER' | 'BANK_API' | 'SYSTEM'
  eventType: 'Create' | 'Edit' | 'Sync' | 'Status Change'
}
const AUDIT_LOG_STORAGE_KEY = 'audit_logs'
// Load audit logs from localStorage
const loadAuditLogs = (): AuditLogEntry[] => {
  try {
    const stored = localStorage.getItem(AUDIT_LOG_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Error loading audit logs:', error)
    return []
  }
}
// Save audit logs to localStorage
const saveAuditLogs = (logs: AuditLogEntry[]): void => {
  try {
    localStorage.setItem(AUDIT_LOG_STORAGE_KEY, JSON.stringify(logs))
  } catch (error) {
    console.error('Error saving audit logs:', error)
  }
}
// In-memory storage for audit logs (synced with localStorage)
let auditLogs: AuditLogEntry[] = loadAuditLogs()
// Generate unique ID for audit log entries
const generateId = (): string => {
  return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}
// Create audit log entry
export const createAuditLog = (
  userId: string,
  userName: string,
  fieldName: string,
  oldValue: string,
  newValue: string,
  source: 'BO_USER' | 'BANK_API' | 'SYSTEM',
  eventType: 'Create' | 'Edit' | 'Sync' | 'Status Change',
): AuditLogEntry => {
  const entry: AuditLogEntry = {
    id: generateId(),
    timestamp: new Date().toISOString(),
    userId,
    userName,
    fieldName,
    oldValue,
    newValue,
    source,
    eventType,
  }
  auditLogs.push(entry)
  saveAuditLogs(auditLogs)
  console.log('Audit log created:', entry)
  return entry
}
// Get all audit logs for a specific user
export const getAuditLogsByUserId = (userId: string): AuditLogEntry[] => {
  return auditLogs.filter((log) => log.userId === userId)
}
// Get all audit logs (for global search)
export const getAllAuditLogs = (): AuditLogEntry[] => {
  return auditLogs
}
// Search audit logs globally
export const searchAuditLogs = (query: string): AuditLogEntry[] => {
  const lowerQuery = query.toLowerCase()
  return auditLogs.filter(
    (log) =>
      log.userName.toLowerCase().includes(lowerQuery) ||
      log.fieldName.toLowerCase().includes(lowerQuery) ||
      log.oldValue.toLowerCase().includes(lowerQuery) ||
      log.newValue.toLowerCase().includes(lowerQuery) ||
      log.eventType.toLowerCase().includes(lowerQuery) ||
      log.source.toLowerCase().includes(lowerQuery) ||
      log.userId.toLowerCase().includes(lowerQuery),
  )
}
// Track user creation
export const logUserCreation = (
  userId: string,
  userName: string,
  userData: any,
): void => {
  console.log('Logging user creation for:', userId, userName, userData)
  // Log the creation event
  createAuditLog(
    userId,
    userName,
    'User Created',
    '',
    'Initial Setup',
    'BO_USER',
    'Create',
  )
  // Log each field that was set during creation
  const fieldsToLog = [
    { name: 'Full Name', value: userData.fullName || userData.name },
    { name: 'Username', value: userData.username },
    { name: 'Email', value: userData.email },
    { name: 'Mobile', value: userData.mobile || userData.phone },
    { name: 'Branch Code', value: userData.branchCode || '' },
    { name: 'Settlement Account', value: userData.settlementAccount || '' },
    { name: 'Designation', value: userData.designation || '' },
    { name: 'Partner', value: userData.partner || '' },
    { name: 'Sub Agent', value: userData.subAgent || '' },
    { name: 'User Type', value: userData.userType || '' },
    { name: 'Status', value: userData.status || 'active' },
  ]
  fieldsToLog.forEach((field) => {
    if (field.value) {
      createAuditLog(
        userId,
        userName,
        field.name,
        '',
        field.value,
        'BO_USER',
        'Create',
      )
    }
  })
}
// Track user edit
export const logUserEdit = (
  userId: string,
  userName: string,
  oldData: any,
  newData: any,
): void => {
  const fieldsToCheck = [
    { name: 'Full Name', oldKey: 'fullName', newKey: 'fullName' },
    { name: 'Name', oldKey: 'name', newKey: 'name' },
    { name: 'Username', oldKey: 'username', newKey: 'username' },
    { name: 'Email', oldKey: 'email', newKey: 'email' },
    { name: 'Mobile', oldKey: 'mobile', newKey: 'mobile' },
    { name: 'Phone', oldKey: 'phone', newKey: 'phone' },
    { name: 'Branch Code', oldKey: 'branchCode', newKey: 'branchCode' },
    {
      name: 'Settlement Account',
      oldKey: 'settlementAccount',
      newKey: 'settlementAccount',
    },
    { name: 'Designation', oldKey: 'designation', newKey: 'designation' },
    { name: 'Partner', oldKey: 'partner', newKey: 'partner' },
    { name: 'Sub Agent', oldKey: 'subAgent', newKey: 'subAgent' },
    { name: 'User Type', oldKey: 'userType', newKey: 'userType' },
    { name: 'Father Name', oldKey: 'fatherName', newKey: 'fatherName' },
    { name: 'CNIC', oldKey: 'cnic', newKey: 'cnic' },
    { name: 'ID Number', oldKey: 'idNumber', newKey: 'idNumber' },
  ]
  fieldsToCheck.forEach((field) => {
    const oldValue = oldData[field.oldKey] || ''
    const newValue = newData[field.newKey] || ''
    if (oldValue !== newValue) {
      createAuditLog(
        userId,
        userName,
        field.name,
        oldValue,
        newValue,
        'BO_USER',
        'Edit',
      )
    }
  })
}
// Track status change
export const logStatusChange = (
  userId: string,
  userName: string,
  oldStatus: string,
  newStatus: string,
): void => {
  createAuditLog(
    userId,
    userName,
    'Status',
    oldStatus,
    newStatus,
    'BO_USER',
    'Status Change',
  )
}
// Track password reset
export const logPasswordReset = (
  userId: string,
  userName: string,
  reason?: string,
): void => {
  // Log password reset event
  createAuditLog(
    userId,
    userName,
    'Password Reset',
    'Active Password',
    'Temporary Password (OTP Generated)',
    'BO_USER',
    'Status Change',
  )
  // Log password reset required flag
  createAuditLog(
    userId,
    userName,
    'Password Reset Required',
    'false',
    'true',
    'SYSTEM',
    'Status Change',
  )
  // Log reason if provided
  if (reason) {
    createAuditLog(
      userId,
      userName,
      'Reset Reason',
      '',
      reason,
      'BO_USER',
      'Status Change',
    )
  }
  // Log email notification
  createAuditLog(
    userId,
    userName,
    'Email Notification',
    '',
    'Password reset email sent with new OTP',
    'SYSTEM',
    'Status Change',
  )
}
// Track email resend
export const logEmailResend = (
  userId: string,
  userName: string,
  recipientEmail: string,
  success: boolean,
  reason?: string,
): void => {
  // Log email resend event
  createAuditLog(
    userId,
    userName,
    'Email Resend',
    '',
    success ? 'Email sent successfully' : 'Email sending failed',
    'BO_USER',
    'Status Change',
  )
  // Log recipient email
  createAuditLog(
    userId,
    userName,
    'Recipient Email',
    '',
    recipientEmail,
    'SYSTEM',
    'Status Change',
  )
  // Log reason if provided
  if (reason) {
    createAuditLog(
      userId,
      userName,
      'Resend Reason',
      '',
      reason,
      'BO_USER',
      'Status Change',
    )
  }
  // Log delivery status
  createAuditLog(
    userId,
    userName,
    'Email Delivery Status',
    '',
    success ? 'SUCCESS' : 'FAILED',
    'SYSTEM',
    'Status Change',
  )
}
// Track sync from external system (Bank API)
export const logSync = (
  userId: string,
  userName: string,
  fieldName: string,
  oldValue: string,
  newValue: string,
): void => {
  createAuditLog(
    userId,
    userName,
    fieldName,
    oldValue,
    newValue,
    'BANK_API',
    'Sync',
  )
}
// Track first login flag change
export const logFirstLoginChange = (
  userId: string,
  userName: string,
  oldValue: boolean,
  newValue: boolean,
): void => {
  createAuditLog(
    userId,
    userName,
    'First Login Required',
    oldValue.toString(),
    newValue.toString(),
    'SYSTEM',
    'Status Change',
  )
}
// Simulate Bank API sync (for demonstration)
export const simulateBankAPISync = (
  userId: string,
  userName: string,
  syncData: {
    branchCode?: string
    settlementAccount?: string
    designation?: string
  },
  currentData: any,
): void => {
  // Log each field that was synced from Bank API
  if (syncData.branchCode && syncData.branchCode !== currentData.branchCode) {
    logSync(
      userId,
      userName,
      'Branch Code',
      currentData.branchCode || '',
      syncData.branchCode,
    )
  }
  if (
    syncData.settlementAccount &&
    syncData.settlementAccount !== currentData.settlementAccount
  ) {
    logSync(
      userId,
      userName,
      'Settlement Account',
      currentData.settlementAccount || '',
      syncData.settlementAccount,
    )
  }
  if (
    syncData.designation &&
    syncData.designation !== currentData.designation
  ) {
    logSync(
      userId,
      userName,
      'Designation',
      currentData.designation || '',
      syncData.designation,
    )
  }
}
// Clear all audit logs (for testing purposes)
export const clearAuditLogs = (): void => {
  auditLogs = []
  saveAuditLogs(auditLogs)
}
// Get audit log statistics
export const getAuditLogStats = () => {
  const stats = {
    total: auditLogs.length,
    byEventType: {
      Create: auditLogs.filter((log) => log.eventType === 'Create').length,
      Edit: auditLogs.filter((log) => log.eventType === 'Edit').length,
      Sync: auditLogs.filter((log) => log.eventType === 'Sync').length,
      'Status Change': auditLogs.filter(
        (log) => log.eventType === 'Status Change',
      ).length,
    },
    bySource: {
      BO_USER: auditLogs.filter((log) => log.source === 'BO_USER').length,
      BANK_API: auditLogs.filter((log) => log.source === 'BANK_API').length,
      SYSTEM: auditLogs.filter((log) => log.source === 'SYSTEM').length,
    },
  }
  return stats
}
// Initialize with some mock data for demonstration
export const initializeMockAuditLogs = (): void => {
  // This would be called on app initialization to populate some sample data
  // In production, this would load from a database
}
